import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getFileUploadSettings,
  updateFileUploadSettings,
  FileUploadSettingsView,
  FileUploadSettingsInput,
} from '../../../api/General/fileUploadSettings';
import StickyActionBar from '../../../components/ui/StickyActionBar';
import { PageLayout } from '../../../components/ui/PageLayout';
import {
  UserCircle,
  FileText,
  Calendar,
  Receipt,
  Camera,
  Bug,
  Building2,
  FileImage,
  Bell,
  Upload,
  Loader2,
  CheckCircle,
  AlertCircle,
  Info,
  FileUp,
} from 'lucide-react';

/** Configuration for each file upload feature row. */
const FEATURE_CONFIGS = [
  { key: 'profilePhotoMaxSizeMB', labelKey: 'profilePhotoMaxSize', icon: UserCircle },
  { key: 'userDocumentMaxSizeMB', labelKey: 'userDocumentMaxSize', icon: FileText },
  { key: 'leaveAttachmentMaxSizeMB', labelKey: 'leaveAttachmentMaxSize', icon: Calendar },
  { key: 'claimAttachmentMaxSizeMB', labelKey: 'claimAttachmentMaxSize', icon: Receipt },
  { key: 'clockingPhotoMaxSizeMB', labelKey: 'clockingPhotoMaxSize', icon: Camera },
  { key: 'bugReportAttachmentMaxSizeMB', labelKey: 'bugReportAttachmentMaxSize', icon: Bug },
  { key: 'companyLogoMaxSizeMB', labelKey: 'companyLogoMaxSize', icon: Building2 },
  { key: 'payslipBrandingLogoMaxSizeMB', labelKey: 'payslipBrandingLogoMaxSize', icon: FileImage },
  { key: 'notificationImageMaxSizeMB', labelKey: 'notificationImageMaxSize', icon: Bell },
  { key: 'batchImportMaxSizeMB', labelKey: 'batchImportMaxSize', icon: Upload },
] as const;

type SettingsKey = (typeof FEATURE_CONFIGS)[number]['key'];

const FileUploadSettings: React.FC = () => {
  const { t } = useTranslation();

  const [settings, setSettings] = useState<FileUploadSettingsView | null>(null);
  const [formValues, setFormValues] = useState<FileUploadSettingsInput | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const showToast = useCallback((type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 5000);
  }, []);

  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getFileUploadSettings();
      if (result.errors.length === 0) {
        setSettings(result.payload);
        setFormValues({
          profilePhotoMaxSizeMB: result.payload.profilePhotoMaxSizeMB,
          userDocumentMaxSizeMB: result.payload.userDocumentMaxSizeMB,
          leaveAttachmentMaxSizeMB: result.payload.leaveAttachmentMaxSizeMB,
          claimAttachmentMaxSizeMB: result.payload.claimAttachmentMaxSizeMB,
          clockingPhotoMaxSizeMB: result.payload.clockingPhotoMaxSizeMB,
          bugReportAttachmentMaxSizeMB: result.payload.bugReportAttachmentMaxSizeMB,
          companyLogoMaxSizeMB: result.payload.companyLogoMaxSizeMB,
          payslipBrandingLogoMaxSizeMB: result.payload.payslipBrandingLogoMaxSizeMB,
          notificationImageMaxSizeMB: result.payload.notificationImageMaxSizeMB,
          batchImportMaxSizeMB: result.payload.batchImportMaxSizeMB,
        });
      } else {
        showToast('error', t('Failed to load settings'));
      }
    } catch (error) {
      console.error('Failed to load file upload settings:', error);
      showToast('error', t('Failed to load settings'));
    } finally {
      setIsLoading(false);
    }
  }, [showToast, t]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const hasUnsavedChanges = (): boolean => {
    if (!settings || !formValues) return false;
    return FEATURE_CONFIGS.some(
      ({ key }) => settings[key] !== formValues[key]
    );
  };

  const handleValueChange = (key: SettingsKey, value: number) => {
    if (!formValues) return;
    const clamped = Math.min(100, Math.max(1, value));
    setFormValues((prev) => (prev ? { ...prev, [key]: clamped } : prev));
  };

  const handleSave = async () => {
    if (!formValues) return;
    setIsSaving(true);
    try {
      const result = await updateFileUploadSettings(formValues);
      if (result.errors.length === 0) {
        setSettings(result.payload);
        setFormValues({
          profilePhotoMaxSizeMB: result.payload.profilePhotoMaxSizeMB,
          userDocumentMaxSizeMB: result.payload.userDocumentMaxSizeMB,
          leaveAttachmentMaxSizeMB: result.payload.leaveAttachmentMaxSizeMB,
          claimAttachmentMaxSizeMB: result.payload.claimAttachmentMaxSizeMB,
          clockingPhotoMaxSizeMB: result.payload.clockingPhotoMaxSizeMB,
          bugReportAttachmentMaxSizeMB: result.payload.bugReportAttachmentMaxSizeMB,
          companyLogoMaxSizeMB: result.payload.companyLogoMaxSizeMB,
          payslipBrandingLogoMaxSizeMB: result.payload.payslipBrandingLogoMaxSizeMB,
          notificationImageMaxSizeMB: result.payload.notificationImageMaxSizeMB,
          batchImportMaxSizeMB: result.payload.batchImportMaxSizeMB,
        });
        showToast('success', t('fileUploadSettingsSaved'));
      } else {
        showToast('error', t('Failed to save settings'));
      }
    } catch (error) {
      console.error('Failed to save file upload settings:', error);
      showToast('error', t('Failed to save settings'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (!settings) return;
    setFormValues({
      profilePhotoMaxSizeMB: settings.profilePhotoMaxSizeMB,
      userDocumentMaxSizeMB: settings.userDocumentMaxSizeMB,
      leaveAttachmentMaxSizeMB: settings.leaveAttachmentMaxSizeMB,
      claimAttachmentMaxSizeMB: settings.claimAttachmentMaxSizeMB,
      clockingPhotoMaxSizeMB: settings.clockingPhotoMaxSizeMB,
      bugReportAttachmentMaxSizeMB: settings.bugReportAttachmentMaxSizeMB,
      companyLogoMaxSizeMB: settings.companyLogoMaxSizeMB,
      payslipBrandingLogoMaxSizeMB: settings.payslipBrandingLogoMaxSizeMB,
      notificationImageMaxSizeMB: settings.notificationImageMaxSizeMB,
      batchImportMaxSizeMB: settings.batchImportMaxSizeMB,
    });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return t('Never');
    return new Date(dateString).toLocaleString();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <PageLayout
      title={t('fileUploadSettings')}
      description={t('fileUploadSettingsDescription')}
      icon={FileUp}
      accentColor="slate"
    >
      <div className="space-y-6">
      {/* Toast Notification */}
      {toast && (
        <div
          className={`fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 ${
            toast.type === 'success'
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {toast.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span className="font-medium">{toast.message}</span>
        </div>
      )}

      {/* Audit Info Bar */}
      {settings?.lastModifiedBy && (
        <div className="flex items-center gap-2 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
          <Info className="w-4 h-4 flex-shrink-0" />
          <span>
            {t('Last modified by')}{' '}
            <strong>{settings.lastModifiedBy}</strong>{' '}
            {t('on')} {formatDate(settings.lastModifiedAt)}
          </span>
        </div>
      )}

      {/* Settings Card */}
      <div className="bg-card rounded-xl border border-border shadow-sm">
        {/* Table Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-gray-50 rounded-t-xl">
          <span className="text-sm font-medium text-gray-600">
            {t('Feature')}
          </span>
          <span className="text-sm font-medium text-gray-600 w-24 text-right">
            {t('maxSizeMB')}
          </span>
        </div>

        {/* Feature Rows */}
        {formValues &&
          FEATURE_CONFIGS.map(({ key, labelKey, icon: Icon }) => (
            <div
              key={key}
              className="flex items-center justify-between px-4 py-3 border-b border-border last:border-0"
            >
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 text-gray-600">
                  <Icon className="w-4 h-4" />
                </div>
                <span className="text-sm font-medium text-gray-800">
                  {t(labelKey)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={formValues[key]}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val)) {
                      handleValueChange(key, val);
                    }
                  }}
                  className="w-24 text-right px-3 py-1.5 text-sm border border-border rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                />
                <span className="text-xs text-gray-400 w-6">MB</span>
              </div>
            </div>
          ))}
      </div>

      {/* Sticky Action Bar */}
      <StickyActionBar
        hasUnsavedChanges={hasUnsavedChanges()}
        isSaving={isSaving}
        onReset={handleReset}
        onSave={handleSave}
        t={t}
      />
      </div>
    </PageLayout>
  );
};

export default FileUploadSettings;
